import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { adminService } from '../../services/loanService';
import { formatDate, formatCurrency } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// ── Shared badge ──────────────────────────────────────────────────────────────

const STATUS_META = {
  pending:      { label: 'Pending',      color: '#ff9900' },
  under_review: { label: 'Under Review', color: '#5B8EFF' },
  approved:     { label: 'Approved',     color: '#00C896' },
  rejected:     { label: 'Rejected',     color: '#FF4757' },
  escalated:    { label: 'Escalated',    color: '#818CF8' },
  executed:     { label: 'Executed',     color: '#00C896' },
};

const PRIORITY_META = {
  low:      { label: 'Low',      color: '#94a3b8' },
  medium:   { label: 'Medium',   color: '#5B8EFF' },
  high:     { label: 'High',     color: '#ff9900' },
  critical: { label: 'Critical', color: '#FF4757' },
};

function Badge({ value, meta }) {
  const m = meta[value] || { label: value, color: '#94a3b8' };
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.6rem',
      borderRadius: 99, textTransform: 'uppercase', letterSpacing: '0.05em',
      background: `${m.color}18`, color: m.color, border: `1px solid ${m.color}30`,
    }}>{m.label}</span>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, color, to, urgent, sub }) {
  const navigate = useNavigate();
  return (
    <div
      className="stat-card"
      onClick={() => to && navigate(to)}
      style={{
        cursor: to ? 'pointer' : 'default',
        transition: 'transform .15s, box-shadow .15s',
        position: 'relative', overflow: 'hidden',
        ...(urgent && { borderColor: `${color}40` }),
      }}
      onMouseEnter={e => { if (to) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 32px ${color}20`; } }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      {urgent && (
        <span style={{
          position: 'absolute', top: 8, right: 8, width: 8, height: 8,
          borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}`,
          animation: 'pulse 2s infinite',
        }} />
      )}
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-label">{label}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', marginTop: '0.2rem' }}>{sub}</div>}
      {to && <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', marginTop: '0.35rem' }}>Click to view →</div>}
    </div>
  );
}

// ── Decision modal ────────────────────────────────────────────────────────────

function DecideModal({ request, onClose }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [decision, setDecision] = useState('approved');
  const [comment, setComment] = useState('');
  const [err, setErr] = useState('');

  const isSelf = request.maker_email === user?.email;
  const noMakerRec = request.module === 'loans' &&
    !['maker', 'both'].includes(request.maker_reviewer_type);

  const mutation = useMutation({
    mutationFn: (d) => adminService.decideApproval(request.id, d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checker-approvals'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
      onClose();
    },
    onError: (e) => setErr(e.response?.data?.message || 'Failed to submit decision.'),
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div className="card" style={{ width: 500, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.25rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Review Request</h2>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginBottom: '1rem' }}>
          {request.reference_number} — {request.title}
        </p>

        <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.875rem', marginBottom: '1rem', fontSize: '0.8rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div><span style={{ color: 'var(--text-3)' }}>Maker: </span><strong>{request.maker_email}</strong></div>
            <div><span style={{ color: 'var(--text-3)' }}>Module: </span><span style={{ textTransform: 'capitalize' }}>{request.module}</span></div>
            <div><span style={{ color: 'var(--text-3)' }}>Action: </span>{request.action}</div>
            <div><span style={{ color: 'var(--text-3)' }}>Priority: </span><Badge value={request.priority} meta={PRIORITY_META} /></div>
          </div>
          {request.description && (
            <p style={{ marginTop: '0.625rem', color: 'var(--text-2)', lineHeight: 1.5 }}>{request.description}</p>
          )}
        </div>

        {isSelf && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            <span>⚠</span>
            <span>Maker-checker violation — you cannot approve your own request.</span>
          </div>
        )}

        {noMakerRec && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            <span>⚠</span>
            <span>This loan has no maker recommendation yet. A maker must approve or decline it first before you can act.</span>
          </div>
        )}

        {request.is_overdue && (
          <div className="alert alert-error" style={{ marginBottom: '1rem', borderColor: '#FF475740' }}>
            <span>⏱</span>
            <span>This request is <strong>overdue</strong>. Please action it immediately.</span>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Your Decision</label>
          <select className="form-input" value={decision} onChange={e => setDecision(e.target.value)} disabled={isSelf || noMakerRec}>
            <option value="approved">Approve</option>
            <option value="rejected">Reject</option>
            <option value="escalated">Escalate</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">
            Comment {decision === 'rejected' ? <span style={{ color: '#FF4757' }}>(required)</span> : '(optional)'}
          </label>
          <textarea
            className="form-input"
            rows={3}
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Add your review notes…"
            disabled={isSelf || noMakerRec}
          />
        </div>

        {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            className="btn btn-primary"
            style={{ flex: 1 }}
            disabled={mutation.isPending || isSelf || noMakerRec || (decision === 'rejected' && !comment.trim())}
            onClick={() => mutation.mutate({ decision, comment })}
          >
            {mutation.isPending ? 'Submitting…' : 'Submit Decision'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Payment Schedule expand ───────────────────────────────────────────────────

const SCHEDULE_STATUS_COLOR = {
  pending: '#ff9900',
  paid:    '#00C896',
  overdue: '#FF4757',
  partial: '#818CF8',
};

function ScheduleExpand({ loanId, highlightScheduleId, colSpan }) {
  const { data, isLoading } = useQuery({
    queryKey: ['checker-payment-schedule', loanId],
    queryFn: () => adminService.getLoanSchedule(loanId).then(r => {
      const d = r.data.data;
      return Array.isArray(d) ? d : d.results ?? [];
    }),
    enabled: !!loanId,
  });

  const schedule = data || [];

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0, background: 'var(--surface-alt)', borderBottom: '2px solid var(--border)' }}>
        {isLoading ? (
          <div style={{ padding: '1rem' }}><LoadingSpinner /></div>
        ) : schedule.length === 0 ? (
          <div style={{ padding: '1rem', fontSize: '0.8rem', color: 'var(--text-3)' }}>No repayment schedule found for this loan.</div>
        ) : (
          <div style={{ padding: '0.75rem 1rem' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>
              Repayment Schedule
            </div>
            <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['#', 'Due Date', 'Amount Due', 'Late Fee', 'Paid', 'Balance', 'Status', 'Paid On'].map(h => (
                    <th key={h} style={{ textAlign: h === '#' || h === 'Status' || h === 'Due Date' || h === 'Paid On' ? 'left' : 'right', padding: '0.25rem 0.5rem', color: 'var(--text-3)', fontWeight: 600, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schedule.map(inst => {
                  const isHighlighted = inst.id === highlightScheduleId;
                  const color = SCHEDULE_STATUS_COLOR[inst.status] || '#94a3b8';
                  return (
                    <tr key={inst.id} style={{
                      borderBottom: '1px solid var(--border)',
                      background: isHighlighted ? 'rgba(129,140,248,0.08)' : 'transparent',
                      borderLeft: isHighlighted ? '3px solid #818CF8' : '3px solid transparent',
                    }}>
                      <td style={{ padding: '0.3rem 0.5rem', fontWeight: isHighlighted ? 700 : 400 }}>{inst.installment_number}</td>
                      <td style={{ padding: '0.3rem 0.5rem', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{formatDate(inst.due_date)}</td>
                      <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(inst.amount_due)}</td>
                      <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: inst.late_fee > 0 ? '#FF4757' : 'var(--text-3)' }}>
                        {inst.late_fee > 0 ? formatCurrency(inst.late_fee) : '—'}
                      </td>
                      <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: '#00C896' }}>{formatCurrency(inst.amount_paid || 0)}</td>
                      <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: (inst.balance || 0) > 0 ? '#FF4757' : 'var(--text-3)' }}>
                        {formatCurrency(inst.balance || 0)}
                      </td>
                      <td style={{ padding: '0.3rem 0.5rem' }}>
                        <span style={{
                          fontSize: '0.62rem', fontWeight: 700, padding: '0.12rem 0.4rem',
                          borderRadius: 99, textTransform: 'uppercase',
                          background: `${color}15`, color, border: `1px solid ${color}30`,
                        }}>
                          {inst.status}
                        </span>
                      </td>
                      <td style={{ padding: '0.3rem 0.5rem', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                        {inst.paid_at ? formatDate(inst.paid_at) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </td>
    </tr>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

export default function CheckerDashboard() {
  const { user, isChecker, loading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [paymentErr, setPaymentErr] = useState({});
  const [expandedPaymentId, setExpandedPaymentId] = useState(null);

  useEffect(() => {
    if (!loading && !isChecker) navigate('/admin/dashboard', { replace: true });
  }, [isChecker, loading, navigate]);

  const { data: approvalsData, isLoading: queueLoading } = useQuery({
    queryKey: ['checker-approvals'],
    queryFn: () => adminService.getApprovals({ status: 'pending' }).then(r => r.data.data),
  });

  const { data: overdueData } = useQuery({
    queryKey: ['checker-overdue'],
    queryFn: () => adminService.getApprovals({ status: 'under_review' }).then(r => r.data.data),
  });

  const { data: paymentsData, isLoading: paymentsLoading } = useQuery({
    queryKey: ['checker-pending-payments'],
    queryFn: () => adminService.getPendingCheckerPayments().then(r => r.data.data),
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const confirmPayment = useMutation({
    mutationFn: ({ loanId, paymentId, decision }) =>
      adminService.confirmPayment(loanId, paymentId, decision),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checker-pending-payments'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
    },
    onError: (e, variables) =>
      setPaymentErr(prev => ({ ...prev, [variables.paymentId]: e.response?.data?.message || 'Failed.' })),
  });

  const pendingItems = approvalsData?.results ?? approvalsData ?? [];
  const overdueItems = (overdueData?.results ?? overdueData ?? []).filter(r => r.is_overdue);
  const pendingPayments = paymentsData?.results ?? paymentsData ?? [];

  const criticalItems = pendingItems.filter(r => r.priority === 'critical' || r.priority === 'high');
  const loanPendingItems = pendingItems.filter(r => r.module === 'loans');

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Checker Dashboard</h1>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>
            Logged in as <strong style={{ color: 'var(--primary)' }}>Checker</strong> — you review and approve maker requests
          </p>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.5rem 1rem',
          background: 'rgba(0,200,150,0.08)', border: '1px solid rgba(0,200,150,0.25)',
          borderRadius: 'var(--radius)', fontSize: '0.8rem',
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00C896', animation: 'pulse 2s infinite', display: 'inline-block' }} />
          <span style={{ color: '#00C896', fontWeight: 700 }}>CHECKER MODE</span>
        </div>
      </div>

      {/* Overdue / critical alerts */}
      {overdueItems.length > 0 && (
        <div
          onClick={() => navigate('/admin/approvals')}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '0.75rem 1rem', marginBottom: '1rem',
            background: 'rgba(255,71,87,0.08)', border: '1px solid rgba(255,71,87,0.3)',
            borderRadius: 'var(--radius)', cursor: 'pointer',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,71,87,0.14)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,71,87,0.08)'}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF4757', animation: 'pulse 2s infinite', flexShrink: 0 }} />
          <span style={{ fontSize: '0.875rem', flex: 1, color: 'var(--text-1)' }}>
            <strong>{overdueItems.length}</strong> overdue request{overdueItems.length > 1 ? 's' : ''} require immediate action.
          </span>
          <span style={{ fontSize: '0.75rem', color: '#FF4757', fontWeight: 600 }}>Review →</span>
        </div>
      )}

      {criticalItems.length > 0 && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '0.75rem 1rem', marginBottom: '1rem',
            background: 'rgba(255,153,0,0.08)', border: '1px solid rgba(255,153,0,0.3)',
            borderRadius: 'var(--radius)',
          }}
        >
          <span style={{ fontSize: '1rem' }}>⚡</span>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-1)' }}>
            <strong>{criticalItems.length}</strong> high-priority request{criticalItems.length > 1 ? 's' : ''} in the queue.
          </span>
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <StatCard
          label="Loans to Review"
          value={queueLoading ? '…' : loanPendingItems.length}
          color="#5B8EFF"
          to="/admin/approvals"
          urgent={loanPendingItems.length > 0}
          sub="Maker-recommended loans"
        />
        <StatCard
          label="All Pending Requests"
          value={queueLoading ? '…' : pendingItems.length}
          color="#ff9900"
          to="/admin/approvals"
          urgent={pendingItems.length > 0}
          sub="Across all modules"
        />
        <StatCard
          label="Payments to Confirm"
          value={paymentsLoading ? '…' : pendingPayments.length}
          color="#818CF8"
          urgent={pendingPayments.length > 0}
          sub="Maker-approved payments"
        />
        <StatCard
          label="Overdue"
          value={overdueItems.length}
          color="#FF4757"
          to="/admin/approvals"
          urgent={overdueItems.length > 0}
          sub="Past SLA deadline"
        />
      </div>

      {/* Pending queue inline */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: 0 }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '0.95rem', fontWeight: 700 }}>Pending Requests</h2>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 2 }}>Requests submitted by makers awaiting your decision</p>
          </div>
          <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }} onClick={() => navigate('/admin/approvals')}>
            View All →
          </button>
        </div>

        {queueLoading ? (
          <div style={{ padding: '2rem' }}><LoadingSpinner /></div>
        ) : pendingItems.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
            <p style={{ color: 'var(--text-2)', fontWeight: 600, marginBottom: '0.25rem' }}>All clear</p>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>No pending requests. Check back soon.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Title</th>
                  <th>Module</th>
                  <th>Maker</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pendingItems.map(req => (
                  <tr key={req.id} style={req.is_overdue ? { background: 'rgba(255,71,87,0.04)' } : {}}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {req.reference_number}
                      {req.is_overdue && (
                        <span style={{ marginLeft: 6, fontSize: '0.6rem', color: '#FF4757', fontWeight: 700 }}>OVERDUE</span>
                      )}
                    </td>
                    <td style={{ maxWidth: 200 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{req.title}</div>
                      {req.description && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 2 }}>
                          {req.description.slice(0, 55)}{req.description.length > 55 ? '…' : ''}
                        </div>
                      )}
                    </td>
                    <td><span style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>{req.module}</span></td>
                    <td style={{ fontSize: '0.8rem' }}>
                      {req.maker_email}
                      {req.maker_email === user?.email && (
                        <span style={{ marginLeft: 4, fontSize: '0.6rem', color: '#FF4757', fontWeight: 700 }}>(you)</span>
                      )}
                    </td>
                    <td><Badge value={req.priority} meta={PRIORITY_META} /></td>
                    <td><Badge value={req.status} meta={STATUS_META} /></td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{formatDate(req.created_at)}</td>
                    <td>
                      <button
                        className={`btn ${req.maker_email === user?.email ? 'btn-ghost' : 'btn-primary'}`}
                        style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem', opacity: req.maker_email === user?.email ? 0.4 : 1 }}
                        disabled={req.maker_email === user?.email}
                        onClick={() => setSelected(req)}
                        title={req.maker_email === user?.email ? 'Cannot approve your own request' : 'Review this request'}
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payments to confirm */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: 0 }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '0.95rem', fontWeight: 700 }}>Payments to Confirm</h2>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 2 }}>
              Maker-approved user payments awaiting your confirmation
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              className="btn btn-ghost"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
              onClick={() => qc.invalidateQueries({ queryKey: ['checker-pending-payments'] })}
            >
              ↻ Refresh
            </button>
            {pendingPayments.length > 0 && (
              <span style={{
                fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem',
                borderRadius: 99, background: 'rgba(129,140,248,0.15)', color: '#818CF8',
                border: '1px solid rgba(129,140,248,0.3)',
              }}>
                {pendingPayments.length} pending
              </span>
            )}
          </div>
        </div>

        {paymentsLoading ? (
          <div style={{ padding: '2rem' }}><LoadingSpinner /></div>
        ) : pendingPayments.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
            <p style={{ color: 'var(--text-2)', fontWeight: 600, marginBottom: '0.25rem' }}>No payments to confirm</p>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>Maker-approved payments will appear here for your final confirmation.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th>Payer</th>
                  <th>Loan</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Maker Approved By</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingPayments.map(p => {
                  const isExpanded = expandedPaymentId === p.id;
                  return (
                    <>
                      <tr key={p.id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{p.payer_name}</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>{p.payer_email}</div>
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--primary)' }}>{p.loan_number}</td>
                        <td style={{ fontWeight: 700 }}>{formatCurrency(p.amount)}</td>
                        <td style={{ fontSize: '0.78rem', textTransform: 'capitalize', color: 'var(--text-2)' }}>
                          {p.method?.replace(/_/g, ' ')}
                        </td>
                        <td style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>
                          {p.maker_reviewed_by_name || '—'}
                        </td>
                        <td style={{ color: 'var(--text-3)', fontSize: '0.78rem' }}>{formatDate(p.paid_at || p.created_at)}</td>
                        <td>
                          {paymentErr[p.id] && (
                            <div style={{ fontSize: '0.7rem', color: '#FF4757', marginBottom: '0.3rem' }}>{paymentErr[p.id]}</div>
                          )}
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <button
                              className="btn btn-ghost"
                              style={{ fontSize: '0.7rem', padding: '0.25rem 0.65rem', color: '#818CF8', borderColor: '#818CF840' }}
                              onClick={() => setExpandedPaymentId(isExpanded ? null : p.id)}
                            >
                              Schedule {isExpanded ? '▴' : '▾'}
                            </button>
                            <button
                              className="btn btn-primary"
                              style={{ fontSize: '0.7rem', padding: '0.25rem 0.65rem', background: '#1596E0', border: 'none' }}
                              disabled={confirmPayment.isPending}
                              onClick={() => {
                                setPaymentErr(prev => ({ ...prev, [p.id]: null }));
                                confirmPayment.mutate({ loanId: p.loan, paymentId: p.id, decision: 'confirm' });
                              }}
                            >
                              Confirm
                            </button>
                            <button
                              className="btn btn-ghost"
                              style={{ fontSize: '0.7rem', padding: '0.25rem 0.65rem', color: '#FF4757', borderColor: '#FF475740' }}
                              disabled={confirmPayment.isPending}
                              onClick={() => {
                                setPaymentErr(prev => ({ ...prev, [p.id]: null }));
                                confirmPayment.mutate({ loanId: p.loan, paymentId: p.id, decision: 'decline' });
                              }}
                            >
                              Decline
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <ScheduleExpand
                          key={`schedule-${p.id}`}
                          loanId={p.loan}
                          highlightScheduleId={p.schedule}
                          colSpan={7}
                        />
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Role reminder */}
      <div style={{
        padding: '0.875rem 1rem',
        background: 'rgba(91,142,255,0.06)', border: '1px solid rgba(91,142,255,0.2)',
        borderRadius: 'var(--radius)', fontSize: '0.8rem', color: 'var(--text-2)',
      }}>
        <strong style={{ color: '#5B8EFF' }}>Checker Role</strong> — You can approve, reject, or escalate requests submitted by makers.
        You cannot initiate actions directly. The maker-checker separation ensures a four-eyes principle on all high-risk operations.
      </div>

      {selected && <DecideModal request={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
